import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AddOutlined,
  AssignmentOutlined,
  DeleteOutline,
  ListAltOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/Api';
import {
  formatDate,
  getApiErrorMessage,
  getClassDisplayName,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalPanelCardSx,
  teacherPortalRowSx,
  toInputDate,
} from './teacherPortalUtils';

export default function TeacherAssignmentsScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    dueDate: toInputDate(new Date()),
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadAssignments = useCallback(async () => {
    if (!selectedClassId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/assignments/${selectedClassId}`);
      setAssignments(res.data?.assignments || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load assignments.'));
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedClassId || !form.title.trim() || !form.dueDate) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/assignments', {
        title: form.title.trim(),
        description: form.description.trim(),
        dueDate: form.dueDate,
        class: selectedClassId,
      });
      toast.success('Assignment created.');
      setForm({ title: '', description: '', dueDate: toInputDate(new Date()) });
      loadAssignments();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create assignment.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await api.delete(`/assignments/${id}`);
      toast.success('Assignment deleted.');
      setAssignments((prev) => prev.filter((assignment) => (assignment._id || assignment.id) !== id));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete assignment.'));
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Assignments</Typography>
          <Typography color="text.secondary">Create and manage class assignments.</Typography>
        </Box>
        <Button startIcon={<RefreshOutlined />} onClick={loadAssignments} disabled={loading} variant="outlined" sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssignmentOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    New Assignment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create work for the selected class.
                  </Typography>
                </Box>
              </Stack>
              <Stack component="form" onSubmit={handleCreate} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Class</InputLabel>
                  <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                    {classes.map((cls) => (
                      <MenuItem key={cls._id} value={cls._id}>{getClassDisplayName(cls)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Title" value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} fullWidth />
                <TextField label="Description" value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} fullWidth multiline minRows={4} />
                <TextField label="Due date" type="date" value={form.dueDate} onChange={(event) => setForm((prev) => ({ ...prev, dueDate: event.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
                <Button type="submit" variant="contained" startIcon={<AddOutlined />} disabled={saving} sx={{ textTransform: 'none', borderRadius: 1 }}>
                  Create Assignment
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} sx={teacherPortalGridItemSx}>
          <Card sx={teacherPortalPanelCardSx(theme)}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <ListAltOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Assignment List ({assignments.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review and manage existing class assignments.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={1.5}>
                  {assignments.map((assignment) => {
                    const id = assignment._id || assignment.id;
                    return (
                      <Box key={id} sx={teacherPortalRowSx(theme)}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="h6" fontWeight={900}>{assignment.title}</Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.5 }}>{assignment.description || 'No description'}</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>Due: {formatDate(assignment.dueDate)}</Typography>
                          </Box>
                          <Button color="error" startIcon={<DeleteOutline />} onClick={() => handleDelete(id)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                            Delete
                          </Button>
                        </Stack>
                      </Box>
                    );
                  })}
                  {!loading && assignments.length === 0 && (
                  <Typography color="text.secondary">No assignments found for this class.</Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
